import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Printer, DownloadSimple, House, MapPin, Ruler, Sun, CloudSun, Moon, FileText, Image, QrCode, Copy, Check } from '@phosphor-icons/react';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

export const ContractorSheetPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [propertyData, setPropertyData] = useState(null);
  const [zoneFiles, setZoneFiles] = useState({});
  const [fileUrls, setFileUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [shareToken, setShareToken] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPropertyData();
    fetchShareLink();
  }, []);

  const fetchShareLink = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/property/share-link`, {}, { withCredentials: true });
      setShareToken(data.share_token);
    } catch (error) {
      console.error('Error fetching share link:', error);
    }
  };

  const getShareUrl = () => {
    if (!shareToken) return null;
    // Use production domain for share links
    return `https://getturfpulse.com/shared/${shareToken}`;
  };

  const handleCopyLink = () => {
    const url = getShareUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const fetchPropertyData = async () => {
    try {
      const { data: property } = await axios.get(`${API_URL}/api/property`, { withCredentials: true });
      setPropertyData(property);
      
      // Fetch files for each zone
      if (property?.zones) {
        const filesMap = {};
        const urlsMap = {};
        for (const zone of property.zones) {
          try {
            const { data: files } = await axios.get(`${API_URL}/api/zones/${zone.id}/files`, { withCredentials: true });
            filesMap[zone.id] = files;
            // Pre-fetch blob URLs for all files (images + PDFs)
            for (const file of files) {
              try {
                const resp = await axios.get(`${API_URL}/api/zones/files/${file.id}/download`, {
                  withCredentials: true,
                  responseType: 'blob'
                });
                urlsMap[file.id] = URL.createObjectURL(resp.data);
              } catch {
                // Skip if download fails
              }
            }
          } catch {
            filesMap[zone.id] = [];
          }
        }
        setZoneFiles(filesMap);
        setFileUrls(urlsMap);
      }
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Failed to load property data');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API_URL}/api/property/contractor-pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Property_Measurements_${propertyData?.name?.replace(/\s+/g, '_') || 'TurfPulse'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const getSunIcon = (exposure) => {
    switch (exposure) {
      case 'Full Sun': return <Sun weight="duotone" size={16} className="text-amber-500" />;
      case 'Partial Shade': return <CloudSun weight="duotone" size={16} className="text-blue-500" />;
      case 'Full Shade': return <Moon weight="duotone" size={16} className="text-indigo-500" />;
      default: return <Sun weight="duotone" size={16} />;
    }
  };

  const totalSquareFootage = propertyData?.zones?.reduce((sum, z) => sum + (z.square_footage || 0), 0) || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading property data...</div>
      </div>
    );
  }

  if (!propertyData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <House weight="duotone" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium">No property data available</p>
            <p className="text-sm text-muted-foreground mt-2">Set up your property first to generate a contractor sheet.</p>
            <Button className="mt-4" onClick={() => navigate('/property')}>Go to Property</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header - Hidden when printing */}
      <header className="bg-background/70 backdrop-blur-xl border-b border-border shadow-sm sticky top-0 z-50 print:hidden">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TurfPulseLogoSVG animated={false} size={36} />
            <h1 className="text-xl font-bold">Contractor Sheet</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint}>
              <Printer weight="bold" size={18} className="mr-2" />
              Print
            </Button>
            <Button variant="default" onClick={handleExportPDF} data-testid="contractor-pdf-btn">
              <FileText weight="bold" size={18} className="mr-2" />
              Download PDF
            </Button>
            <Button variant="outline" onClick={() => navigate('/property')}>
              <ArrowLeft weight="bold" size={18} className="mr-2" />
              Back
            </Button>
          </div>
        </div>
      </header>

      {/* Printable Content */}
      <div className="max-w-4xl mx-auto p-6 print:p-4 print:max-w-none">
        {/* Header with branding */}
        <div className="text-center mb-8 print:mb-4">
          <div className="flex items-center justify-center gap-3 mb-4 print:mb-2">
            <TurfPulseLogoSVG animated={false} size={56} />
            <span className="text-3xl font-bold text-[#0D9256] print:text-2xl">TurfPulse</span>
          </div>
          <h1 className="text-xl font-medium text-muted-foreground print:text-lg">Property Measurement Summary</h1>
          <p className="text-muted-foreground">Prepared for Lawn Care Contractors</p>
          <p className="text-xs text-muted-foreground mt-2">
            Generated: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Property Overview */}
        <Card className="mb-6 print:shadow-none print:border-gray-300">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <House weight="duotone" size={20} className="text-primary" />
              Property Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6">
              <div className="flex-1 grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Property Name</p>
                  <p className="font-bold text-lg">{propertyData.name || 'My Property'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Location</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin weight="fill" size={14} className="text-red-500" />
                    {[propertyData.address, propertyData.city, propertyData.state, propertyData.zip].filter(Boolean).join(', ') || 'Not specified'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Grass Type</p>
                  <p className="font-medium capitalize">{user?.grass_type?.replace('-', ' ') || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Contact</p>
                  <p className="font-medium">{user?.name || 'Property Owner'}</p>
                  {user?.email && <p className="text-sm text-muted-foreground">{user.email}</p>}
                </div>
              </div>
              
              {/* QR Code */}
              {shareToken && (
                <div className="flex flex-col items-center justify-center p-3 bg-white rounded-lg border print:border-gray-300">
                  <QRCodeSVG 
                    value={getShareUrl()} 
                    size={100}
                    fgColor="#0D9256"
                    level="L"
                  />
                  <p className="text-xs text-muted-foreground mt-2 text-center">Scan to view<br/>on mobile</p>
                </div>
              )}
            </div>
            
            {/* Share Link (hidden when printing) */}
            {shareToken && (
              <div className="mt-4 pt-4 border-t print:hidden">
                <div className="flex items-center gap-2">
                  <QrCode weight="bold" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Share Link:</span>
                  <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">{getShareUrl()}</code>
                  <Button variant="outline" size="sm" onClick={handleCopyLink}>
                    {copied ? <Check weight="bold" size={14} /> : <Copy weight="bold" size={14} />}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Total Square Footage Highlight */}
            <div className="mt-6 p-4 bg-primary/10 rounded-lg border-2 border-primary/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Lawn Area</p>
                  <p className="text-3xl font-bold text-primary">{totalSquareFootage.toLocaleString()} sq ft</p>
                </div>
                <Ruler weight="duotone" size={48} className="text-primary/50" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {propertyData.zones?.length || 0} zone{propertyData.zones?.length !== 1 ? 's' : ''} measured
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Zone Details */}
        <Card className="mb-6 print:shadow-none print:border-gray-300">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Zone Measurements</CardTitle>
            <CardDescription>Individual lawn areas with square footage</CardDescription>
          </CardHeader>
          <CardContent>
            {propertyData.zones?.length > 0 ? (
              <div className="space-y-4">
                {propertyData.zones.map((zone, idx) => (
                  <div key={zone.id} className="border rounded-lg p-4 print:break-inside-avoid">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-bold text-lg">{zone.name || `Zone ${idx + 1}`}</h3>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                          {zone.sun_exposure && (
                            <span className="flex items-center gap-1">
                              {getSunIcon(zone.sun_exposure)}
                              {zone.sun_exposure}
                            </span>
                          )}
                        </div>
                        {zone.notes && (
                          <p className="text-sm text-muted-foreground mt-2 italic">"{zone.notes}"</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-primary">
                          {(zone.square_footage || 0).toLocaleString()}
                        </p>
                        <p className="text-sm text-muted-foreground">sq ft</p>
                      </div>
                    </div>
                    
                    {/* Zone Images */}
                    {zoneFiles[zone.id]?.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                          <Image weight="bold" size={14} />
                          Zone Images / Measurements
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {zoneFiles[zone.id].slice(0, 6).map((file) => (
                            <div key={file.id} className="relative group">
                              <a
                                href={fileUrls[file.id] || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                  if (!fileUrls[file.id]) {
                                    e.preventDefault();
                                    toast.error('File not available');
                                  }
                                }}
                                data-testid={`zone-file-link-${file.id}`}
                              >
                                {file.content_type?.startsWith('image/') && fileUrls[file.id] ? (
                                  <img
                                    src={fileUrls[file.id]}
                                    alt={file.original_filename}
                                    className="w-full h-24 object-cover rounded border"
                                  />
                                ) : (
                                  <div className="w-full h-24 bg-muted rounded border flex flex-col items-center justify-center hover:bg-muted/80 transition-colors">
                                    <FileText weight="duotone" size={24} className="text-muted-foreground" />
                                    <span className="text-[10px] text-primary mt-1 font-medium">View File</span>
                                  </div>
                                )}
                              </a>
                              <p className="text-xs text-muted-foreground truncate mt-1">{file.original_filename}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Ruler weight="duotone" size={48} className="mx-auto mb-4 opacity-50" />
                <p>No zones measured yet</p>
                <p className="text-sm mt-1">Add zones to your property to include them here.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Measurement Summary Table */}
        {propertyData.zones?.length > 0 && (
          <Card className="mb-6 print:shadow-none print:border-gray-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Quick Reference</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium">Zone</th>
                    <th className="text-right py-2 font-medium">Square Footage</th>
                    <th className="text-right py-2 font-medium">% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {propertyData.zones.map((zone, idx) => (
                    <tr key={zone.id} className="border-b">
                      <td className="py-2">{zone.name || `Zone ${idx + 1}`}</td>
                      <td className="py-2 text-right font-mono">{(zone.square_footage || 0).toLocaleString()} sq ft</td>
                      <td className="py-2 text-right text-muted-foreground">
                        {totalSquareFootage > 0 ? Math.round((zone.square_footage || 0) / totalSquareFootage * 100) : 0}%
                      </td>
                    </tr>
                  ))}
                  <tr className="font-bold bg-muted/50">
                    <td className="py-2">TOTAL</td>
                    <td className="py-2 text-right font-mono">{totalSquareFootage.toLocaleString()} sq ft</td>
                    <td className="py-2 text-right">100%</td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Notes for Contractor */}
        <Card className="mb-6 print:shadow-none print:border-gray-300 border-dashed">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Notes for Contractor</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="min-h-[100px] border-2 border-dashed rounded-lg p-4 text-muted-foreground text-sm">
              <p className="italic">Space for contractor notes, quotes, or special instructions...</p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground mt-8 print:mt-4">
          <p>Measurements powered by TurfPulse - Smart Lawn Care</p>
          <p className="text-xs mt-1">getturfpulse.com</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border-gray-300 { border-color: #d1d5db !important; }
          .print\\:p-4 { padding: 1rem !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:mb-4 { margin-bottom: 1rem !important; }
          .print\\:mb-2 { margin-bottom: 0.5rem !important; }
          .print\\:text-xl { font-size: 1.25rem !important; }
          .print\\:mt-4 { margin-top: 1rem !important; }
          .print\\:break-inside-avoid { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default ContractorSheetPage;
